/**
 * The identity view: resolved full-name and identifier clusters, their members,
 * and — for every merge — the evidence that licensed it.
 *
 * Two things here are load-bearing.
 *
 * **A `proposed` link is visibly unreviewed, never rendered like a confirmed one.**
 * `entity-resolution-links` design D3: a link is live from the moment it is written,
 * and the state has to be legible everywhere it appears, not just in the export
 * bundle. The marker on an unreviewed member is deliberately louder than the one on a
 * confirmed one.
 *
 * **Confirming or rejecting is one action**, affordable because only a handful of
 * identities recur across investigations in a real corpus — the whole reason a human
 * review step is viable here rather than a rule this codebase would otherwise have to
 * trust blindly.
 */

import { useCallback, useMemo, useState } from "react";
import {
  ApiError,
  api,
  type ExcludedCandidate,
  type Identity,
  type IdentityDetail,
  type RejectedLink,
  type Suggestion,
} from "../lib/api";
import { useEvidence } from "../state/evidence";
import { useGatedQuery } from "../state/renderGate";

/** The list surface's three shapes, discriminated so the render gate can hold a
 * single `{query, rows}` pair (design D1) even though "Rejected" and "Excluded" each
 * read an entirely different endpoint (design D3 / `identity-shape-gate` design D3)
 * from every other filter value. */
type ListResult =
  | { kind: "identities"; rows: Identity[] }
  | { kind: "rejected"; rows: RejectedLink[] }
  | { kind: "excluded"; rows: ExcludedCandidate[] };

export function Identities({ datasetId }: { datasetId: string }) {
  const { select, selected } = useEvidence();
  const [stateFilter, setStateFilter] = useState<string>("");
  const [detail, setDetail] = useState<IdentityDetail | null>(null);
  // D2: a rejected singleton's post-verdict 404 is the success signal that its
  // cluster no longer projects, not an error — this is the terminal state that
  // renders instead of `detail`, carrying the verdict and who decided it.
  const [removed, setRemoved] = useState<{ verdict: string; decidedBy: string } | null>(
    null,
  );
  // Detail-pane errors are their own channel (task 3.2): `loadIdentities()` used to
  // call `setError(null)` as its first line, which silently cleared an error
  // `openIdentity()` had just set one line earlier. A list refresh may only ever
  // clear an error the list surface itself owns.
  const [detailError, setDetailError] = useState<string | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // identity-review-triage design D5: marking never filters — it only changes
  // where a marked row sits in the list, and only when a reviewer asks for it.
  const [sortMarkedLast, setSortMarkedLast] = useState(false);
  // Suggestion-rail errors are their own channel too, so a Confirm/Dismiss click
  // never silently clears an error the identity detail pane just set (the same
  // "a caller may clear only errors it owns" rule as `detailError` above).
  const [suggestionError, setSuggestionError] = useState<string | null>(null);
  // Excluded-candidate confirm errors are their own channel too, for the same reason.
  const [excludedError, setExcludedError] = useState<string | null>(null);

  const query = useMemo(() => ({ datasetId, stateFilter }), [datasetId, stateFilter]);
  const fetchList = useCallback(async (active: typeof query): Promise<ListResult> => {
    // D3: "Rejected" is an audit query over the verdict record, not a filter over
    // `entity_members_current` — that projection structurally excludes a rejected
    // singleton's only link (D1: no non-rejected link left, no member left), which is
    // exactly why the old filter could never show one.
    if (active.stateFilter === "rejected") {
      return { kind: "rejected", rows: await api.rejectedLinks(active.datasetId) };
    }
    // `identity-shape-gate` design D3: "Excluded" reads the recomputed shape-gate
    // projection, never `entity_members_current` — an excluded pair never has a
    // member row to read there in the first place.
    if (active.stateFilter === "excluded") {
      return { kind: "excluded", rows: await api.excludedCandidates(active.datasetId) };
    }
    return {
      kind: "identities",
      rows: await api.identities(active.datasetId, active.stateFilter || undefined),
    };
  }, []);
  const { rows: listResult, error: listError, reload: loadIdentities } = useGatedQuery(
    query,
    fetchList,
  );
  const error = runError ?? listError;

  const identityRows = listResult?.kind === "identities" ? listResult.rows : null;
  const rejectedRows = listResult?.kind === "rejected" ? listResult.rows : null;
  const excludedRows = listResult?.kind === "excluded" ? listResult.rows : null;

  // The triage rail's own query — independent of the identity-list facet above, so
  // switching the review-state filter never touches the rail and vice versa.
  const suggestionQuery = useMemo(() => ({ datasetId }), [datasetId]);
  const fetchSuggestions = useCallback(
    (active: typeof suggestionQuery) => api.suggestions(active.datasetId, "open"),
    [],
  );
  const {
    rows: suggestionRows,
    error: suggestionListError,
    reload: loadSuggestions,
  } = useGatedQuery(suggestionQuery, fetchSuggestions);

  const sortedIdentityRows = useMemo(() => {
    if (!identityRows || !sortMarkedLast) return identityRows;
    // Stable partition, never a filter: every row that was in the list stays in
    // it, marked rows simply move to the end (design D5, "sortable, never hidden").
    const unmarked = identityRows.filter((i) => !i.non_name_shape);
    const marked = identityRows.filter((i) => i.non_name_shape);
    return [...unmarked, ...marked];
  }, [identityRows, sortMarkedLast]);

  async function openIdentity(clusterId: string) {
    setDetailError(null);
    try {
      const d = await api.identity(datasetId, clusterId);
      setDetail(d);
      setRemoved(null);
    } catch (cause) {
      setDetailError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  async function runResolution() {
    setBusy(true);
    setRunError(null);
    try {
      const started = await api.startResolution(datasetId);
      for (let attempt = 0; attempt < 60; attempt += 1) {
        const run = await api.extraction(datasetId, started.run_id);
        if (run.status === "ready" || run.status === "failed") break;
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
      await loadIdentities();
    } catch (cause) {
      setRunError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  async function decide(linkId: string, verdict: "confirmed" | "rejected") {
    setDetailError(null);
    try {
      const result = await api.submitVerdict(datasetId, linkId, verdict);
      if (detail) {
        try {
          const refreshed = await api.identity(datasetId, detail.cluster_id);
          setDetail(refreshed);
          setRemoved(null);
        } catch (cause) {
          if (cause instanceof ApiError && cause.status === 404) {
            // D2: the true answer to "what does this identity look like now" is
            // "it no longer projects" — a singleton whose only link was just
            // rejected has no non-rejected link left, so no member left, so no
            // cluster left. Render that fact, sourced from the verdict response
            // itself, never a swallowed console-only error.
            setDetail(null);
            setRemoved({ verdict: result.state, decidedBy: result.decided_by });
          } else {
            setDetailError(cause instanceof Error ? cause.message : String(cause));
          }
        }
      }
      await loadIdentities();
    } catch (cause) {
      setDetailError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  async function decideSuggestion(
    suggestion: Suggestion,
    verdict: "confirmed" | "dismissed",
  ) {
    setSuggestionError(null);
    try {
      await api.submitSuggestionVerdict(datasetId, suggestion.suggestion_id, verdict);
      await loadSuggestions();
      // A confirm merges two identities into one — the list this rail sits beside
      // has to reflect that immediately too, not just the rail itself.
      await loadIdentities();
    } catch (cause) {
      setSuggestionError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  async function confirmExcluded(candidate: ExcludedCandidate) {
    setExcludedError(null);
    try {
      await api.confirmExcludedCandidate(datasetId, candidate.pair_key);
      // The confirmed pair leaves the Excluded projection (it now has a real link)
      // and the identity it just formed appears in the ordinary listing — reload
      // whichever facet is currently on screen, mirroring `decideSuggestion` above.
      await loadIdentities();
    } catch (cause) {
      setExcludedError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  return (
    <div className="page">
      <h1>Identities</h1>

      <div className="row">
        <button
          className="primary"
          onClick={runResolution}
          disabled={busy}
          data-testid="run-resolution"
        >
          {busy ? "Resolving…" : "Run resolution"}
        </button>
        <select
          aria-label="Filter by review state"
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value)}
          data-testid="identity-state-filter"
        >
          <option value="">All states</option>
          <option value="proposed">Proposed</option>
          <option value="confirmed">Confirmed</option>
          <option value="rejected">Rejected</option>
          <option value="excluded">Excluded</option>
        </select>
        <label>
          <input
            type="checkbox"
            checked={sortMarkedLast}
            onChange={(e) => setSortMarkedLast(e.target.checked)}
            data-testid="sort-marked-last"
          />{" "}
          Sort non-name shapes last
        </label>
      </div>

      {error && (
        <p data-testid="identities-error" style={{ color: "var(--danger)" }}>
          {error}
        </p>
      )}
      {suggestionListError && (
        <p data-testid="suggestions-error" style={{ color: "var(--danger)" }}>
          {suggestionListError}
        </p>
      )}
      {suggestionError && (
        <p data-testid="suggestion-decide-error" style={{ color: "var(--danger)" }}>
          {suggestionError}
        </p>
      )}
      {excludedError && (
        <p data-testid="excluded-decide-error" style={{ color: "var(--danger)" }}>
          {excludedError}
        </p>
      )}

      {/* design D6: the triage rail — part of the identity list, not a new view.
          Open suggestions face each other with their own evidence, so a reviewer
          never has to go hunting an arbitrary number of prominence-ordered rows
          apart for the pair a rule already flagged. */}
      {suggestionRows && suggestionRows.length > 0 && (
        <section data-testid="suggestion-rail">
          <h2>Suggested merges</h2>
          <ol className="hits" data-testid="suggestion-list">
            {suggestionRows.map((suggestion) => (
              <li key={suggestion.suggestion_id}>
                <div className="hit" data-testid="suggestion-row">
                  <span className="eyebrow" data-testid="suggestion-meta">
                    {suggestion.rule_class} · rule {suggestion.rule_version}
                  </span>
                  <div className="row">
                    {[suggestion.side_a, suggestion.side_b].map((side) => (
                      <div key={side.cluster_id} data-testid="suggestion-side">
                        <p className="hit-text mono" data-testid="suggestion-side-value">
                          {side.display_value}
                        </p>
                        <p className="eyebrow">
                          prominence {side.prominence} · <span className="mono">{side.pair_key}</span>
                        </p>
                        <ol className="hits" data-testid="suggestion-side-evidence">
                          {side.evidence.map((item) => (
                            <li key={item.span_id}>
                              <button
                                className="hit"
                                data-testid="suggestion-evidence-item"
                                onClick={() =>
                                  select({
                                    blockId: item.block_id,
                                    spans: [
                                      {
                                        start_cp: item.start_cp,
                                        end_cp: item.end_cp,
                                        quote: item.quote,
                                      },
                                    ],
                                    label: side.display_value,
                                    origin: "identity",
                                  })
                                }
                              >
                                <span className="hit-text mono">{item.quote}</span>
                              </button>
                            </li>
                          ))}
                        </ol>
                      </div>
                    ))}
                  </div>
                  <span className="row">
                    <button
                      data-testid="confirm-suggestion"
                      onClick={() => void decideSuggestion(suggestion, "confirmed")}
                    >
                      Confirm
                    </button>
                    <button
                      data-testid="dismiss-suggestion"
                      onClick={() => void decideSuggestion(suggestion, "dismissed")}
                    >
                      Dismiss
                    </button>
                  </span>
                </div>
              </li>
            ))}
          </ol>
        </section>
      )}

      <div className="workspace two-pane">
        {rejectedRows === null && excludedRows === null && (
          <ol className="hits" data-testid="identity-list">
            {sortedIdentityRows?.map((identity) => (
              <li key={identity.cluster_id}>
                <button
                  className="hit"
                  data-testid="identity"
                  aria-current={detail?.cluster_id === identity.cluster_id}
                  onClick={() => void openIdentity(identity.cluster_id)}
                >
                  <span className="eyebrow" data-testid="identity-meta">
                    {identity.anchor_kind} · prominence {identity.prominence} ·{" "}
                    {identity.member_count}{" "}
                    {identity.member_count === 1 ? "member" : "members"}
                  </span>
                  <span className="hit-text mono" data-testid="identity-value">
                    {identity.display_value}
                  </span>
                  <span
                    data-testid="identity-state"
                    className={
                      identity.state === "proposed" ? "gap" : "eyebrow"
                    }
                  >
                    {identity.state === "proposed"
                      ? "⚠ proposed — unreviewed"
                      : identity.state}
                  </span>
                  {identity.non_name_shape && (
                    <span data-testid="identity-non-name-shape" className="gap">
                      ⚠ not name-shaped ({identity.non_name_shape_version})
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ol>
        )}

        {/* D3: the Rejected filter is an audit surface over the verdict record, not
            a click-through list — each row already carries its own evidence and
            verdict metadata, because the cluster it once belonged to may no longer
            exist to click through to. */}
        {rejectedRows !== null && (
          <ol className="hits" data-testid="rejected-link-list">
            {rejectedRows.map((link) => (
              <li key={link.link_id}>
                <div className="hit" data-testid="rejected-link">
                  <span className="eyebrow" data-testid="rejected-link-meta">
                    {link.anchor_kind} · rejected by {link.decided_by} ·{" "}
                    {new Date(link.decided_at).toLocaleString()}
                  </span>
                  <span className="hit-text mono" data-testid="rejected-link-value">
                    {link.display_value}
                  </span>
                  <span data-testid="rejected-link-state" className="eyebrow">
                    {link.verdict}
                  </span>
                  {link.evidence.length > 0 && (
                    <ol className="hits" data-testid="rejected-link-evidence">
                      {link.evidence.map((item) => (
                        <li key={item.span_id}>
                          <button
                            className="hit"
                            data-testid="rejected-link-evidence-item"
                            aria-current={selected?.blockId === item.block_id}
                            onClick={() =>
                              select({
                                blockId: item.block_id,
                                spans: [
                                  {
                                    start_cp: item.start_cp,
                                    end_cp: item.end_cp,
                                    quote: item.quote,
                                  },
                                ],
                                label: link.display_value,
                                origin: "identity",
                              })
                            }
                          >
                            <span className="hit-text mono">{item.quote}</span>
                          </button>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}

        {/* `identity-shape-gate` design D3: the Excluded filter renders the
            recomputed shape-gate projection — each pair with its evidence and fired
            check, and a Confirm control routing through the existing human-anchor
            merge path (the only way one of these ever becomes an ordinary
            identity). */}
        {excludedRows !== null && (
          <ol className="hits" data-testid="excluded-candidate-list">
            {excludedRows.map((candidate) => (
              <li key={candidate.pair_key}>
                <div className="hit" data-testid="excluded-candidate">
                  <span className="eyebrow" data-testid="excluded-candidate-meta">
                    {candidate.fired_check} · gate {candidate.gate_version}
                  </span>
                  <span className="hit-text mono" data-testid="excluded-candidate-value">
                    {candidate.display_value}
                  </span>
                  {candidate.evidence.length > 0 && (
                    <ol className="hits" data-testid="excluded-candidate-evidence">
                      {candidate.evidence.map((item) => (
                        <li key={item.span_id}>
                          <button
                            className="hit"
                            data-testid="excluded-candidate-evidence-item"
                            aria-current={selected?.blockId === item.block_id}
                            onClick={() =>
                              select({
                                blockId: item.block_id,
                                spans: [
                                  {
                                    start_cp: item.start_cp,
                                    end_cp: item.end_cp,
                                    quote: item.quote,
                                  },
                                ],
                                label: candidate.display_value,
                                origin: "identity",
                              })
                            }
                          >
                            <span className="hit-text mono">{item.quote}</span>
                          </button>
                        </li>
                      ))}
                    </ol>
                  )}
                  <button
                    data-testid="confirm-excluded"
                    onClick={() => void confirmExcluded(candidate)}
                  >
                    Confirm
                  </button>
                </div>
              </li>
            ))}
          </ol>
        )}

        {/* D3: every empty state names which of "never ran" and "no matches" is
            actually true, rather than one misdescription covering every filter. */}
        {identityRows?.length === 0 && stateFilter === "" && (
          <p data-testid="identities-empty" style={{ color: "var(--ink-faint)" }}>
            No resolution has run yet. Run resolution once tier-1 person extraction
            has produced full-name pairs.
          </p>
        )}
        {identityRows?.length === 0 && stateFilter !== "" && (
          <p data-testid="identities-empty" style={{ color: "var(--ink-faint)" }}>
            No identities match this filter.
          </p>
        )}
        {rejectedRows?.length === 0 && (
          <p data-testid="identities-empty" style={{ color: "var(--ink-faint)" }}>
            Nothing has been rejected in this dataset.
          </p>
        )}
        {excludedRows?.length === 0 && (
          <p data-testid="identities-empty" style={{ color: "var(--ink-faint)" }}>
            No candidates are currently excluded by the shape gate.
          </p>
        )}

        {detailError && (
          <p data-testid="identity-detail-error" style={{ color: "var(--danger)" }}>
            {detailError}
          </p>
        )}

        {removed && (
          <section className="pane" data-testid="identity-removed">
            <h2>Rejected — removed from the projection</h2>
            <p className="eyebrow">
              verdict: <span data-testid="identity-removed-verdict">{removed.verdict}</span>
              {removed.decidedBy ? ` · decided by ${removed.decidedBy}` : ""}
            </p>
            <p style={{ color: "var(--ink-faint)" }}>
              This identity's only link was just rejected. A singleton cluster whose
              only link is rejected has no member left, so it no longer projects —
              not an error, and not still open for a second decision.
            </p>
          </section>
        )}

        {detail && (
          <section className="pane" data-testid="identity-detail">
            <h2>{detail.display_value}</h2>
            <p className="eyebrow">
              {detail.mentions} mention{detail.mentions === 1 ? "" : "s"} ·
              state:{" "}
              <span data-testid="identity-detail-state">{detail.state}</span>
            </p>

            <h3>Members</h3>
            <ul data-testid="member-list">
              {detail.members.map((member) => (
                <li key={member.link_id}>
                  <span className="mono">{member.member_value}</span>{" "}
                  <span className="eyebrow">
                    ({member.anchor_kind} ·{" "}
                    {member.state === "proposed" ? (
                      <span data-testid="proposed-marker">
                        ⚠ proposed — unreviewed
                      </span>
                    ) : (
                      member.state
                    )}
                    )
                  </span>
                  {member.state === "proposed" && (
                    <span className="row">
                      <button
                        data-testid="confirm-link"
                        onClick={() => void decide(member.link_id, "confirmed")}
                      >
                        Confirm
                      </button>
                      <button
                        data-testid="reject-link"
                        onClick={() => void decide(member.link_id, "rejected")}
                      >
                        Reject
                      </button>
                    </span>
                  )}
                </li>
              ))}
            </ul>

            <h3>Merge evidence</h3>
            <ol className="hits" data-testid="evidence-list">
              {detail.evidence.map((item) => (
                <li key={`${item.link_id}:${item.span_id}`}>
                  <button
                    className="hit"
                    data-testid="evidence-item"
                    aria-current={selected?.blockId === item.block_id}
                    onClick={() =>
                      select({
                        blockId: item.block_id,
                        spans: [
                          {
                            start_cp: item.start_cp,
                            end_cp: item.end_cp,
                            quote: item.quote,
                          },
                        ],
                        label: detail.display_value,
                        origin: "identity",
                      })
                    }
                  >
                    <span className="eyebrow">{item.anchor_kind}</span>
                    <span className="hit-text mono">{item.quote}</span>
                  </button>
                </li>
              ))}
            </ol>
          </section>
        )}
      </div>
    </div>
  );
}
